import React, { useState } from 'react';
import { MessageSquarePlus, X, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    setError(null);
    try {
      await axios.post('/api/v1/feedback', {
        subject: subject.trim() || 'User Feedback',
        message: message.trim(),
        page: window.location.pathname,
      });
      setSent(true);
      setTimeout(() => {
        setOpen(false);
        setSent(false);
        setSubject('');
        setMessage('');
      }, 2000);
    } catch (err) {
      setError('Failed to send — try emailing support@flowize.xyz directly.');
    } finally {
      setSending(false);
    }
  };

  // Floating button
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50"
        title="Send feedback"
      >
        <MessageSquarePlus size={22} />
      </button>
    );
  }

  // Feedback form
  return (
    <div className="fixed bottom-6 right-6 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-blue-600 text-white">
        <span className="font-semibold text-sm">Send Feedback</span>
        <button onClick={() => setOpen(false)} className="hover:bg-blue-700 rounded p-1">
          <X size={16} />
        </button>
      </div>

      {sent ? (
        <div className="p-6 text-center">
          <CheckCircle size={32} className="text-green-500 mx-auto mb-2" />
          <p className="text-sm text-gray-700 font-medium">Thanks for your feedback!</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject (optional)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What's on your mind? Bug reports, feature requests, or general feedback..."
              rows={4}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex items-center justify-between">
            <a href="mailto:support@flowize.xyz" className="text-xs text-gray-400 hover:text-gray-600">
              or email us directly
            </a>
            <button
              type="submit"
              disabled={sending || !message.trim()}
              className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send size={14} />
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
